<template>
  <div>
    <v-btn
      rounded
      size="small"
      class="primary-inverted-button"
      @click="onAdd"
    >
      <v-icon
        tag="span"
        start
      >
        mdi-plus
      </v-icon>
      {{ $t("commonStrings.add") }}
    </v-btn>
    <v-dialog
      :key="message.infoMessageId"
      v-model="display"
      max-width="500"
      persistent
    >
      <v-card loading="isSaving">
        <v-card-title>{{ title }}</v-card-title>
        <v-card-text>
          <vee-form ref="infoMessageObserver">
            <v-container>
              <v-row>
                <v-col cols="12">
                  <vee-field
                    v-slot="{ componentField, errors }"
                    v-model="message.title"
                    rules="max:200"
                    :name="$t('infoMessagesStrings.headers.title')"
                  >
                    <v-text-field
                      v-bind="componentField"
                      :error-messages="errors"
                      :label="$t('infoMessagesStrings.headers.title')"
                      counter="200"
                      variant="outlined"
                      density="compact"
                    />
                  </vee-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <vee-field
                    v-slot="{ componentField, errors }"
                    v-model="message.messageText"
                    rules="max:4000|required"
                    :name="$t('infoMessagesStrings.headers.message')"
                  >
                    <v-textarea
                      v-bind="componentField"
                      :error-messages="errors"
                      :label="$t('infoMessagesStrings.headers.message')"
                      counter="4000"
                      variant="outlined"
                      density="compact"
                    />
                  </vee-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <text-field-date-picker
                    v-model="message.displayFromDate"
                    :label="$t('infoMessagesStrings.headers.fromDate')"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <text-field-date-picker
                    v-model="message.displayToDate"
                    :label="$t('infoMessagesStrings.headers.toDate')"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-select
                    v-model="message.infoMessageType"
                    density="compact"
                    variant="outlined"
                    :items="getInfoMessageTypeOptions()"
                    :label="$t('infoMessagesStrings.headers.type')"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-select
                    v-model="message.recipientType"
                    density="compact"
                    variant="outlined"
                    :items="getInfoMessageRecipientTypeOptions()"
                    :label="$t('infoMessagesStrings.headers.recipient')"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <vee-field
                    v-slot="{ componentField, errors }"
                    v-model="message.readMoreUrl"
                    rules="max:300"
                    :name="$t('infoMessagesStrings.headers.url')"
                  >
                    <v-text-field
                      v-bind="componentField"
                      :error-messages="errors"
                      :label="$t('infoMessagesStrings.headers.url')"
                      counter="300"
                      variant="outlined"
                      density="compact"
                    />
                  </vee-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-select
                    v-model="message.displayOnPage"
                    density="compact"
                    variant="outlined"
                    :items="getRouteOptions()"
                    :label="$t('infoMessagesStrings.headers.displayOnPage')"
                  />
                </v-col>
              </v-row>
            </v-container>
          </vee-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            rounded
            class="primary-inverted-button mb-2 mb-sm-0"
            :block="xs"
            size="small"
            :disabled="isSaving"
            :loading="isSaving"
            @click="cancel"
          >
            {{ $t("commonStrings.cancel") }}
          </v-btn>
          <v-btn
            rounded
            class="primary-button ml-0 ml-sm-2"
            :block="xs"
            size="small"
            :disabled="isSaving"
            :loading="isSaving"
            @click="save"
          >
            {{ $t("commonStrings.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { compare } from "fast-json-patch";
import { mapGetters } from "vuex";
import { Form as VeeForm, Field as VeeField } from "vee-validate";
import TextFieldDatePicker from "@/views/common/components/TextFieldDatePicker";
import InfoMessageTypes from "@/enums/InfoMessageTypes";
import InfoMessageRecipientTypes from "@/enums/InfoMessageRecipientTypes";
import { api, urls } from "@/utils/api";
import router from "@/plugins/router";
import { copy } from "@/utils/object";
import { handleError } from "@/utils/errorHandler";
import { useDisplay } from 'vuetify';
import { useSnacks } from '@/composables/snacks';

export default {
  name: "InfoMessageEditDialog",
  components: {
    VeeForm,
    VeeField,
    TextFieldDatePicker
  },
  props: {
    message: {
      required: false
    }
  },
  setup() {
    const { xs } = useDisplay();
    const { addSnackbar } = useSnacks();
    return { xs, addSnackbar };
  },
  data() {
    return {
      display: false,
      initial: {}
    };
  },
  methods: {
    onAdd() {
      this.$emit("addNew");
    },
    async save() {
      const isDataValid = await this.$refs.infoMessageObserver.validate();

      if (!isDataValid) {
        this.showValidationErrorSnackbar(this.$refs.infoMessageObserver);
        return;
      }

      if (this.message.infoMessageId) {
        await this.patchExisting();
      } else {
        await this.postNew();
      }
    },
    async postNew() {
      try {
        const response = await api.v1.infoMessages.post(this.message);

        if (response.status === 200) {
          this.successResponse();
        }
      } catch (error) {
        await handleError(error);
      }
    },
    async patchExisting() {
      try {
        const patchDoc = compare(this.initial, this.message);
        const response = await api.v1.infoMessages.patch(
          this.message.infoMessageId,
          patchDoc
        );
        if (response.status === 204) {
          this.successResponse();
        }
      } catch (error) {
        await handleError(error);
      }
    },
    successResponse() {
      this.addSnackbar(this.$t("infoMessagesStrings.saved"), "success");
      this.$emit("saved");
    },
    cancel() {
      this.$emit("cancelled");
    },
    getInfoMessageTypeOptions() {
      return Object.values(InfoMessageTypes).map(x => ({
        value: x,
        title: this.$t(`enums.infoMessageTypes.${x}`)
      }));
    },
    getInfoMessageRecipientTypeOptions() {
      return Object.values(InfoMessageRecipientTypes).map(x => ({
        value: x,
        title: this.$t(`enums.infoMessageRecipientTypes.${x}`)
      }));
    },
    getRouteOptions() {
      const routes = router.options.routes.map(x => ({
        value: x.name,
        title: x.name
      }));
      routes.unshift({ value: "", title: this.$t("commonStrings.notDefined") });
      return routes;
    }
  },
  computed: {
    ...mapGetters({
      urlState: "utilsRestStore/urlState"
    }),
    isPosting() {
      return this.urlState(urls.v1.infoMessages.post());
    },
    isPatching() {
      return this.urlState(
        urls.v1.infoMessages.patch(this.message.infoMessageId)
      );
    },
    isSaving() {
      return this.isPosting || this.isPatching;
    },
    title() {
      if (this.message.infoMessageId <= 0) {
        return this.$t("infoMessagesStrings.editModal.createTitle");
      }
      return this.$t("infoMessagesStrings.editModal.editTitle");
    }
  },
  watch: {
    message: {
      handler(val) {
        if (val.infoMessageId >= 0) {
          this.display = !!val;
        } else {
          this.display = false;
        }
      },
      deep: true
    },
    display(val) {
      if (val) {
        this.initial = copy(this.message);
      }
    }
  }
};
</script>
